import { useDebounce } from '@avalenti89/react-ts-utils';
import classNames from 'classnames';
import { Modal } from '../../Modal/Modal';
import { genericMessages } from '../../../../generic.i18n';
import { useContext, useEffect, useState, type JSX } from 'react';
import { useIntl } from 'react-intl';
import { styled } from '@mui/material/styles';
import { frameMessages } from '../i18n';
import { configurationContext } from 'src/contexts/configuration';

export const Footer = styled(
	({
		children,
		onClick,
		className,
		...props
	}: JSX.IntrinsicElements['footer']) => {
		const [isModalOpen, setModalOpen] = useState(false);

		const [count, trigger] = useDebounce(1000, true);
		useEffect(() => {
			if (count === 5) {
				setModalOpen(true);
			}
		}, [count]);

		const intl = useIntl();
		const poweredByText = intl.formatMessage(frameMessages.footer__powered_by);
		const { powered_by } = useContext(configurationContext);

		return (
			<footer
				className={classNames('chat__footer', className)}
				{...props}
				onClick={(e) => {
					onClick?.(e);
					trigger();
				}}
			>
				{children}
				{powered_by === false ? null : (
					<div className="footer__powered-by">
						<span>{poweredByText}</span>
						{powered_by ? (
							<a
								href={powered_by.url}
								target="_blank"
								rel="noopener noreferrer"
							>
								{powered_by.name}
							</a>
						) : (
							<a
								href="https://www.enuan.com"
								target="_blank"
								rel="noopener noreferrer"
							>
								<strong className="enuan-logo">
									<span className="enuan-logo__e">e</span>
									<span className="enuan-logo__nuan">nuan</span>
								</strong>
							</a>
						)}
					</div>
				)}

				<VersionModal
					isOpen={isModalOpen}
					onClose={() => setModalOpen(false)}
				/>
			</footer>
		);
	},
	{
		label: 'Footer',
		target: 'Footer-root',
	},
)({
	display: 'flex',
	flexDirection: 'column',
	maxHeight: 'max(30%, 7em)',
	zIndex: 1,
	position: 'relative',
	backgroundColor: 'var(--chat__footer_background)',
	flexShrink: 0,
	'& .footer__powered-by': {
		display: 'flex',
		justifyContent: 'flex-end',
		backgroundColor: 'var(--chat__footer__powered-by_background)',
		alignItems: 'flex-end',
		zIndex: 11,
		borderTop: 'solid 1px var(--chat__footer__powered-by_border)',
		fontFamily: 'Roboto, sans-serif',
		fontWeight: 400,
		color: 'var(--chat__footer__powered-by_color)',
		padding: '0 0.2em 0.4em',
		fontSize: '1.1em',
		'& .enuan-logo': {
			fontFamily: 'Ubuntu, sans-serif',
			marginTop: 0,
			marginBottom: 0,
			fontWeight: 'bold',
			margin: '0.2em 0.4em',
			'& .enuan-logo__e': {
				color: '#3399ff',
			},
			'& .enuan-logo__nuan': {
				color: '#666666',
			},
		},
		'& a': {
			textDecoration: 'none',
			color: 'unset',
		},
	},
});

const VersionModal = styled(
	({
		isOpen,
		onClose,
		className,
	}: {
		isOpen: boolean;
		onClose: () => void;
		className?: string;
	}) => {
		const intl = useIntl();
		const version = intl.formatMessage(genericMessages.version);
		return (
			<Modal
				open={isOpen}
				onClose={onClose}
				title={version}
				className={classNames(className, 'version_modal')}
			>
				<ul>
					<li>
						<strong>App:</strong>{' '}
						<span>v{process.env.REACT_APP_VERSION_APP}</span>
					</li>
					<li>
						<strong>Lib:</strong>{' '}
						<span>v{process.env.REACT_APP_VERSION_LIB}</span>
					</li>
					<li>
						<strong>Core:</strong>{' '}
						<span>v{process.env.REACT_APP_VERSION_UTILS}</span>
					</li>
				</ul>
			</Modal>
		);
	},
	{
		label: 'VersionModal',
		target: 'VersionModal-root',
	},
)({
	'& .modal__body': {
		padding: '0 2em',
	},
	'& li': {
		fontSize: '1.3em',
		listStyle: 'none',
		display: 'flex',
		justifyContent: 'space-between',
		'& strong': {
			marginRight: '0.5em',
		},
	},
});
